// Configurações de campanhas (modal aberto de /campaigns), mockup Aeros
// "Config Campanhas A": card Intervalos entre disparos (risco de bloqueio +
// "1.000 mensagens levam ≈"), card Janela de envio (início/fim + dias
// Dom–Sáb) e card Variáveis personalizadas. Grava em /campaign-settings:
// messageInterval, longerIntervalAfter, greaterInterval, variables,
// sabado, domingo, startHour, endHour e sendingDays (novo: lista de dias
// 0–6 permitidos; backend lê com fallback para sabado/domingo).
import React, { useEffect, useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Select from "@material-ui/core/Select";
import CloseIcon from "@material-ui/icons/Close";
import TimerIcon from "@material-ui/icons/Timer";
import ScheduleIcon from "@material-ui/icons/Schedule";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import InsightsIcon from "@material-ui/icons/Timeline";
import CodeIcon from "@material-ui/icons/Code";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { estimateSeconds, durationLabel, WEEKDAY_LONG, dailyLimitSummary } from "../CampaignModal/campaignRules";

const tc = (k, o) => i18n.t(`aeros.campaign.config.${k}`, o);

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const border = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const purple = stateText(theme, "#C08BFF", "purple");
  return {
    paper: { width: 820, maxWidth: "calc(100vw - 32px)", margin: 16 },
    body: { padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 14, maxHeight: "70vh", overflowY: "auto", ...theme.scrollbarStyles },
    card: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 },
    head: { display: "flex", alignItems: "center", gap: 8, "& > .MuiSvgIcon-root": { fontSize: 18 } },
    title: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary },
    hint: { fontSize: 11, color: theme.palette.text.secondary },
    risk: { marginLeft: "auto", fontSize: 11, color: theme.palette.text.secondary, display: "flex", alignItems: "center", gap: 5, "& .MuiSvgIcon-root": { fontSize: 14 } },
    grid3: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
    label: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 6, display: "block" },
    field: { backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 10, padding: "9px 12px", fontSize: 13, display: "flex", alignItems: "center", minHeight: 44, boxSizing: "border-box", "& input": { flex: 1, minWidth: 0, background: "none", border: "none", outline: "none", font: "inherit", color: theme.palette.text.primary, colorScheme: theme.palette.type, fontVariantNumeric: "tabular-nums" }, "&:focus-within": { borderColor: AEROS.cyan, boxShadow: "0 0 0 3px rgba(34,211,238,0.15)" }, [theme.breakpoints.down("sm")]: { minHeight: 44, "& input": { fontSize: 16 } } },
    sel: { fontSize: 13, color: theme.palette.text.primary, flex: 1, "& .MuiNativeSelect-select": { padding: 0, paddingRight: 20, background: "transparent", "&:focus": { background: "transparent" } }, "& .MuiNativeSelect-icon": { fontSize: 16, color: theme.palette.aeros.textTertiary, top: "calc(50% - 8px)" } },
    // minmax(0, 1fr) + width 0: o input numérico tem ~170px intrínsecos e,
    // com 1fr puro, os dois campos alargavam a grade além do modal em 375px.
    limitGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 12, "& input": { width: 0 }, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" } },
    limitChannel: { fontSize: 11, fontWeight: 700, color: theme.palette.text.secondary, marginRight: 8, flexShrink: 0, textTransform: "uppercase", letterSpacing: 0.4 },
    fieldHint: { fontSize: 11, color: theme.palette.aeros.textTertiary, marginTop: 5, lineHeight: 1.4 },
    insight: { display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", backgroundColor: theme.palette.background.paper, border: `1px solid ${line}`, borderRadius: 10, fontSize: 12, color: theme.palette.text.secondary, "& .MuiSvgIcon-root": { fontSize: 16, color: AEROS.cyan }, "& b": { color: theme.palette.text.primary } },
    windowGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, alignItems: "end", [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr 1fr" }, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
    // Dias e limite por canal ocupam a linha inteira quando a grade encolhe:
    // espremidos numa coluna, o placeholder do limite era cortado em 768px.
    daysCell: { [theme.breakpoints.down("sm")]: { gridColumn: "1 / -1" } },
    limitCell: { gridColumn: "1 / -1" },
    days: { display: "flex", gap: 4 },
    day: { flex: 1, textAlign: "center", fontSize: 11, fontWeight: 700, borderRadius: 8, padding: "9px 0", color: theme.palette.aeros.textTertiary, background: "none", border: `1px solid ${line}`, cursor: "pointer", font: "inherit" },
    dayOn: { color: purple, backgroundColor: alpha("#A044FF", 0.15), borderColor: "rgba(160,68,255,0.4)" },
    // A 375px "1fr 2fr auto" resolvia para 192+192+64px e estourava o card:
    // no celular cada campo (e o botao Adicionar) ocupa a linha inteira.
    varRow: { display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 8, alignItems: "center", [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
    chip: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, borderRadius: 8, padding: "5px 10px", backgroundColor: theme.palette.background.paper, border: `1px solid ${line}`, color: theme.palette.text.primary, "& code": { color: stateText(theme, AEROS.cyan, "info"), fontFamily: "ui-monospace, monospace" }, "& span": { color: theme.palette.text.secondary, fontWeight: 500 }, "& .MuiIconButton-root": { padding: 2 }, "& .MuiSvgIcon-root": { fontSize: 15, color: AEROS.error } },
    chips: { display: "flex", gap: 6, flexWrap: "wrap" },
  };
});

const parseVariables = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) { try { const p = JSON.parse(value); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};
const parseDays = (value, sabado, domingo) => {
  if (typeof value === "string" && value) { try { const p = JSON.parse(value); if (Array.isArray(p)) return p.map(Number); } catch { /* fallback */ } }
  const base = [1, 2, 3, 4, 5];
  if (sabado) base.push(6);
  if (domingo) base.unshift(0);
  return base;
};
const riskLevel = (messageInterval, greaterInterval) => (messageInterval >= 20 && greaterInterval >= 60 ? "riskLow" : messageInterval >= 10 ? "riskMedium" : "riskHigh");

const SECONDS = [5, 10, 15, 20, 30, 45, 60, 90, 120];
const AFTER = [10, 20, 30, 50, 100];
const LONG = [30, 60, 90, 120, 180, 300];

const CampaignsConfigModal = ({ open, onClose }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [messageInterval, setMessageInterval] = useState(20);
  const [longerIntervalAfter, setLongerIntervalAfter] = useState(20);
  const [greaterInterval, setGreaterInterval] = useState(60);
  const [variables, setVariables] = useState([]);
  const [days, setDays] = useState([1, 2, 3, 4, 5]);
  const [startHour, setStartHour] = useState("08:00");
  const [endHour, setEndHour] = useState("19:00");
  const [dailyLimit, setDailyLimit] = useState("");
  const [dailyLimitSms, setDailyLimitSms] = useState("");
  const [variable, setVariable] = useState({ key: "", value: "" });
  const [removing, setRemoving] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/campaign-settings").then(({ data }) => {
      if (!Array.isArray(data)) return;
      const s = {}; data.forEach((it) => { s[it.key] = it.value; });
      setMessageInterval(Number(s.messageInterval) || 20);
      setLongerIntervalAfter(Number(s.longerIntervalAfter) || 20);
      setGreaterInterval(Number(s.greaterInterval) || 60);
      setVariables(parseVariables(s.variables));
      setDays(parseDays(s.sendingDays, s.sabado === "true", s.domingo === "true"));
      if (s.startHour) setStartHour(s.startHour);
      if (s.endHour) setEndHour(s.endHour);
      const comoCampo = (valor) => { const n = Number(String(valor ?? "").replace(/"/g, "")); return Number.isFinite(n) && n > 0 ? String(Math.floor(n)) : ""; };
      setDailyLimit(comoCampo(s.dailyLimit));
      // Sem dailyLimitSms gravado o SMS herda o limite do WhatsApp: mostrar
      // esse valor é mostrar o que vale hoje, e salvar o torna explícito.
      setDailyLimitSms(comoCampo(s.dailyLimitSms === undefined || s.dailyLimitSms === null ? s.dailyLimit : s.dailyLimitSms));
      setDirty(false);
    }).catch((err) => toastError(err));
  }, [open]);

  const mark = (setter) => (value) => { setter(value); setDirty(true); };

  const addVariable = () => {
    const key = variable.key.trim().replace(/[{}]/g, "");
    if (!key || variables.some((v) => v.key === key)) return;
    setVariables((prev) => [...prev, { key, value: variable.value }]);
    setVariable({ key: "", value: "" });
    setDirty(true);
  };
  const removeVariable = () => { setVariables((prev) => prev.filter((v) => v.key !== removing)); setRemoving(null); setDirty(true); };

  const handleSave = async () => {
    try {
      await api.post("/campaign-settings", { settings: { messageInterval, longerIntervalAfter, greaterInterval, variables, sabado: days.includes(6), domingo: days.includes(0), startHour, endHour, sendingDays: days, dailyLimit: Number(dailyLimit) > 0 ? Math.floor(Number(dailyLimit)) : 0, dailyLimitSms: Number(dailyLimitSms) > 0 ? Math.floor(Number(dailyLimitSms)) : 0 } });
      toast.success(i18n.t("campaigns.settings.savedToast"));
      setDirty(false);
      onClose();
    } catch (err) {
      toastError(err);
    }
  };
  const requestClose = () => { if (removing !== null || discardOpen) return; if (dirty) { setDiscardOpen(true); return; } onClose(); };

  const planoSettings = { messageInterval, longerIntervalAfter, greaterInterval, dailyLimit: Number(dailyLimit) || 0, startHour, endHour, sendingDays: days };
  // Cada canal tem o proprio limite diario: a estimativa sai por canal.
  const estimativa = (limite) => { try { return durationLabel(estimateSeconds(1000, { ...planoSettings, dailyLimit: Number(limite) || 0 })); } catch { return "—"; } };
  const textoDoResumo = (resumo) => {
    if (resumo.mode === "spread") return tc("dailyLimitSpread", { n: resumo.fitPerDay, start: startHour, end: endHour, gap: durationLabel(resumo.gapSeconds) });
    if (resumo.mode === "floor") return tc("dailyLimitFloor", { min: messageInterval, fit: resumo.fitPerDay });
    return tc("dailyLimitOff");
  };
  const limitesPorCanal = [
    { key: "whatsapp", label: tc("channelWhatsapp"), value: dailyLimit, set: setDailyLimit },
    { key: "sms", label: tc("channelSms"), value: dailyLimitSms, set: setDailyLimitSms }
  ].map((c) => ({ ...c, resumo: dailyLimitSummary({ ...planoSettings, dailyLimit: Number(c.value) || 0 }) }));
  const semDias = limitesPorCanal[0].resumo.mode === "noDays";
  const Field = ({ label, value, onChange, options, unitKey, hint }) => (
    <div>
      <span className={classes.label}>{label}</span>
      <div className={classes.field}>
        <Select native disableUnderline className={classes.sel} value={value} onChange={(e) => onChange(Number(e.target.value))} inputProps={{ "aria-label": label }}>
          {options.map((n) => <option key={n} value={n}>{tc(unitKey, { n })}</option>)}
        </Select>
      </div>
      <div className={classes.fieldHint}>{hint}</div>
    </div>
  );

  return (
    <Dialog open={open} onClose={requestClose} maxWidth={false} classes={{ paper: classes.paper }} aria-labelledby="campaigns-config-dialog-title">
      <ConfirmationModal title={i18n.t("campaigns.confirmationModal.deleteTitle")} open={removing !== null} onClose={() => setRemoving(null)} onConfirm={removeVariable}>
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal title={i18n.t("campaigns.settings.discardTitle")} open={discardOpen} onClose={() => setDiscardOpen(false)} onConfirm={() => { setDiscardOpen(false); setDirty(false); onClose(); }}>
        {i18n.t("campaigns.settings.discardMessage")}
      </ConfirmationModal>
      <div className={m.form}>
        <div className={m.header}>
          <div style={{ flex: 1 }}>
            <div className={m.title} id="campaigns-config-dialog-title">{tc("title")}</div>
            <div className={m.subtitle}>{tc("subtitle")}</div>
          </div>
          <Button className={m.submitButton} onClick={handleSave}>{tc("save")}</Button>
          <IconButton className={m.closeButton} onClick={requestClose} aria-label={i18n.t("campaigns.settings.close")}><CloseIcon /></IconButton>
        </div>
        <div className={classes.body}>
          <div className={classes.card}>
            <div className={classes.head}>
              <TimerIcon style={{ color: "#C08BFF" }} /><div className={classes.title}>{tc("intervals")}</div>
              <div className={classes.risk}><VerifiedUserIcon style={{ color: riskLevel(messageInterval, greaterInterval) === "riskLow" ? AEROS.success : AEROS.warning }} />{tc("risk", { level: tc(riskLevel(messageInterval, greaterInterval)) })}</div>
            </div>
            <div className={classes.grid3}>
              <Field label={tc("random")} value={messageInterval} onChange={mark(setMessageInterval)} options={SECONDS.includes(messageInterval) ? SECONDS : [...SECONDS, messageInterval].sort((a, b) => a - b)} unitKey="seconds" hint={tc("randomHint")} />
              <Field label={tc("after")} value={longerIntervalAfter} onChange={mark(setLongerIntervalAfter)} options={AFTER.includes(longerIntervalAfter) ? AFTER : [...AFTER, longerIntervalAfter].sort((a, b) => a - b)} unitKey="messagesN" hint={tc("afterHint")} />
              <Field label={tc("long")} value={greaterInterval} onChange={mark(setGreaterInterval)} options={LONG.includes(greaterInterval) ? LONG : [...LONG, greaterInterval].sort((a, b) => a - b)} unitKey="seconds" hint={tc("longHint")} />
            </div>
            <div className={classes.insight} data-testid="campaigns-config-insight" aria-live="polite"><InsightsIcon /><span>{tc("insight")} <b>{tc("channelWhatsapp")} ≈ {estimativa(dailyLimit)}</b> · <b>{tc("channelSms")} ≈ {estimativa(dailyLimitSms)}</b>.</span></div>
          </div>

          <div className={classes.card}>
            <div className={classes.head}><ScheduleIcon style={{ color: AEROS.cyan }} /><div className={classes.title}>{tc("window")}</div><div className={classes.hint}>{tc("windowHint")}</div></div>
            <div className={classes.windowGrid}>
              <div><span className={classes.label}>{tc("start")}</span><div className={classes.field}><input type="time" value={startHour} onChange={(e) => mark(setStartHour)(e.target.value)} aria-label={tc("start")} /></div></div>
              <div><span className={classes.label}>{tc("end")}</span><div className={classes.field}><input type="time" value={endHour} onChange={(e) => mark(setEndHour)(e.target.value)} aria-label={tc("end")} /></div></div>
              <div className={classes.daysCell}>
                <span className={classes.label}>{tc("days")}</span>
                <div className={classes.days}>
                  {WEEKDAY_LONG().map((d, i) => {
                    const on = days.includes(i);
                    return <button key={i} type="button" className={`${classes.day} ${on ? classes.dayOn : ""}`} aria-pressed={on} onClick={() => mark(setDays)(on ? days.filter((x) => x !== i) : [...days, i].sort())}>{d}</button>;
                  })}
                </div>
              </div>
              <div className={classes.limitCell}>
                <span className={classes.label} id="campaigns-config-daily-limit-label">{tc("dailyLimit")}</span>
                <div className={classes.limitGrid} role="group" aria-labelledby="campaigns-config-daily-limit-label">
                  {limitesPorCanal.map((c) => (
                    <div key={c.key} className={classes.field}>
                      <span className={classes.limitChannel} aria-hidden="true">{c.label}</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        value={c.value}
                        placeholder={tc("dailyLimitPlaceholder")}
                        onChange={(e) => mark(c.set)(e.target.value.replace(/\D/g, ""))}
                        aria-label={tc("dailyLimitChannelAria", { channel: c.label })}
                        aria-describedby="campaigns-config-daily-limit-hint"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className={classes.fieldHint} id="campaigns-config-daily-limit-hint" aria-live="polite">
              {semDias
                ? <div>{tc("dailyLimitNoDays")}</div>
                : limitesPorCanal.map((c) => <div key={c.key}><b>{c.label}:</b> {textoDoResumo(c.resumo)}</div>)}
              <div>{tc("dailyLimitNote")}</div>
            </div>
          </div>

          <div className={classes.card}>
            <div className={classes.head}><CodeIcon style={{ color: AEROS.success }} /><div className={classes.title}>{tc("variables")}</div><div className={classes.hint}>{tc("variablesHint")}</div></div>
            <div className={classes.varRow}>
              <div className={classes.field}><input value={variable.key} onChange={(e) => { const valor = e.target.value; setVariable((v) => ({ ...v, key: valor })); }} placeholder={tc("key")} aria-label={tc("key")} /></div>
              <div className={classes.field}><input value={variable.value} onChange={(e) => { const valor = e.target.value; setVariable((v) => ({ ...v, value: valor })); }} placeholder={tc("value")} aria-label={tc("value")} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addVariable())} /></div>
              <Button className={m.cancelButton} onClick={addVariable} disabled={!variable.key.trim()}>{tc("add")}</Button>
            </div>
            {variables.length > 0 && (
              <div className={classes.chips}>
                {variables.map((v) => (
                  <span key={v.key} className={classes.chip}><code>{`{${v.key}}`}</code><span>{v.value}</span><IconButton size="small" onClick={() => setRemoving(v.key)} aria-label={`${i18n.t("campaigns.confirmationModal.deleteTitle")} ${v.key}`}><DeleteOutlineIcon /></IconButton></span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default CampaignsConfigModal;
